// =====================================================================================
// useRoomConnection Hook - WebSocket Room Connection Management
// =====================================================================================
// Manages WebSocket connection and room state for multiplayer rooms
// Phaser management has been extracted to usePhaserGame and PhaserRoomService

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { io, Socket } from 'socket.io-client';
import type { 
  Room, 
  PlayerData, 
  WebSocketEvents,
  ClientWebSocketEvents,
  WebSocketConnectionConfig
} from '../game/types';
import type { PrivateAreaUpdate, RoomRoleChangedEvent } from 'minka-shared';
import type { HomeZoneClaimPayload, HomeZoneClaimState } from '@/types/room.types';
import { computeGuestTimerDelay } from '@/utils/guest-access';
import { applyRoomRoleChange, publishRoomRoleChange } from '@/utils/room-role-events';
import { WEBSOCKET_CONFIG } from '../game/config';
import type { PlayerUpdateType } from '../game/managers/RoomManager';
import { 
  enrichPlayerData, 
  updatePhaserZones, 
  updatePhaserPrivateAreas,
  refreshPlayerNameTag,
  handlePhaserDoorUpdate
} from '../utils/phaser-sync';
import {
  safeEmit,
  createStatusChangePayload,
  createTeleportPayload,
  createDoorTogglePayload
} from '../utils/websocket-events';
import { usePhaserGame } from './usePhaserGame';
import { phaserRoomService } from '../services/phaser-room.service';
import { useGameInstance } from '../contexts/GameInstanceContext';
import { clearActiveSocketId, setActiveSocketId } from '../services/socket-session';
import { readTokenVersion } from '../utils/token-version';

// =====================================================================================
// TYPE DEFINITIONS
// =====================================================================================

/** How long a caller waits for the socket to report its own close before giving up on it. */
const LEAVE_TIMEOUT_MS = 1000;

/**
 * Private area data structure from WebSocket events
 */
interface PrivateAreaData {
  areaId: string;
  name?: string;
  isDoorOpen?: boolean;
  occupants?: Array<{
    userId: string;
    username: string;
  }>;
}

/**
 * The `error` codes with which the server turns a `join_room` down. Each ends
 * the join at once: waiting for the join timeout instead would leave the caller
 * (the island list, or the session-replaced modal taking a session back)
 * spinning, then reporting a timeout rather than the reason.
 */
const JOIN_REFUSAL_CODES = new Set(['ACCESS_DENIED', 'ROOM_NOT_FOUND', 'GUEST_EXPIRED', 'ROOM_FULL', 'JOIN_ERROR']);

/**
 * Pending status change promise tracking
 */
interface PendingStatusChange {
  resolve: () => void;
  reject: (error: Error) => void;
  timeoutId: NodeJS.Timeout;
}

/**
 * WebSocket room state payload structure
 */
interface RoomState {
  room: Room;
  players: PlayerData[];
  zones: HomeZoneClaimPayload[];
  privateAreas?: PrivateAreaData[];
}

/**
 * Internal hook state for room connection
 */
interface RoomConnectionState {
  isConnected: boolean;
  currentRoom: Room | null;
  playersData: PlayerData[];
  homeZones: HomeZoneClaimState[];
  privateAreas: PrivateAreaData[];
  isLoading: boolean;
  error: string | null;
  /**
   * The session was taken over by the same account on another device or tab
   * (#132). The island stays on screen, inert, until the user takes it back or
   * leaves; nothing re-joins by itself.
   */
  sessionReplaced: boolean;
}

/**
 * Return interface for the useRoomConnection hook
 * Provides all connection state, player data, and room management methods
 */
export interface UseRoomConnectionReturn extends RoomConnectionState {
  /** Join a room by ID, establishing WebSocket connection and loading room data */
  joinRoom: (roomId: string) => Promise<void>;
  /**
   * Take a replaced session back: join the island on screen again, which ends
   * the session on the other device and spawns where that one stood.
   */
  reclaimSession: () => Promise<void>;
  /**
   * Leave the current room, cleaning up Phaser scene content. Resolves once the
   * socket has closed, so a caller can act on the island having been left.
   */
  leaveRoom: () => Promise<void>;
  /** Send a WebSocket message to the server (used by Phaser for real-time updates) */
  sendMessage: (event: string, data: any) => void;
  /** Change the current user's status (available, busy, dnd) via WebSocket */
  changeUserStatus: (status: string) => Promise<void>;
  /** Teleport a player to a specific position (admin only) */
  teleportPlayer: (targetUserId: string, position: { x: number; y: number }) => Promise<void>;
  /** Clean up all WebSocket connections and reset state */
  cleanup: () => void;
  // Zone management methods
  /** Claim a home zone for the current user */
  claimZone: (zoneId: string) => Promise<void>;
  /** Unclaim a home zone owned by the current user */
  unclaimZone: (zoneId: string) => Promise<void>;
  // Private area methods
  /** Toggle a private area door open/closed */
  togglePrivateAreaDoor: (areaId: string) => Promise<void>;
  /** Direct access to the WebSocket connection for additional event handling */
  socket: Socket | null;
}

// =====================================================================================
// MAIN HOOK
// =====================================================================================

/**
 * useRoomConnection Hook - WebSocket Room Connection Management
 * 
 * This is the primary hook for managing multiplayer room connections. It handles:
 * - WebSocket connection lifecycle and real-time event processing
 * - Room state management (players, zones, private areas)
 * - User actions (status changes, teleportation, zone management)
 * - Coordination with Phaser rendering via PhaserRoomService
 * 
 * Phaser game management has been extracted to usePhaserGame hook and
 * PhaserRoomService for better separation of concerns.
 * 
 * @param auth - Authentication state containing current user
 * @param roomContext - Optional context for updating current room in routing
 * @returns Complete room connection interface with state and methods
 * 
 * @example
 * ```tsx
 * const connection = useRoomConnection(auth, { setCurrentRoom });
 * 
 * // Join a room
 * await connection.joinRoom('room-123');
 * 
 * // Access real-time data
 * const players = connection.playersData;
 * const zones = connection.homeZones;
 * 
 * // Perform actions
 * await connection.changeUserStatus('busy');
 * await connection.claimZone('zone-456');
 * ```
 */
export function useRoomConnection(
  auth: { user: { id: string } | null; logout?: () => void },
  roomContext?: { setCurrentRoom: (room: any) => void },
  /**
   * Where an involuntary exit says why it happened. It has to outlive this
   * hook's own state: every message below is delivered *as* the island — or
   * the session — goes away, so `connectionState.error` would be rendered by
   * chrome that is unmounting. See `useNotice`.
   */
  notice?: { setNotice: (message: string) => void }
): UseRoomConnectionReturn {

  // =====================================================================================
  // STATE INITIALIZATION & REFS
  // =====================================================================================
  
  const [connectionState, setConnectionState] = useState<RoomConnectionState>({
    isConnected: false,
    currentRoom: null,
    playersData: [],
    homeZones: [],
    privateAreas: [],
    isLoading: false,
    error: null,
    sessionReplaced: false
  });
  
  const socketRef = useRef<Socket<WebSocketEvents, ClientWebSocketEvents> | null>(null);
  const noticeRef = useRef(notice);
  noticeRef.current = notice;
  const reconnectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  /**
   * The current user's guest expiry on the island they are in, or null for an
   * admin, a resident, or nobody at all. It seeds the courtesy timer of §6.2
   * and comes from the server's own answer to `join_room`.
   */
  const [guestExpiresAt, setGuestExpiresAt] = useState<string | null>(null);
  // Track pending status change promises
  const pendingStatusChangeRef = useRef<PendingStatusChange | null>(null);
  // Track the type of the last player data update to determine how to handle Phaser updates
  const lastUpdateTypeRef = useRef<PlayerUpdateType>('full');

  // Phaser game management via new hook
  const { isGameReady, createGame, setCanvasVisible } = usePhaserGame(auth.user?.id);
  const { gameInstance } = useGameInstance();

  // Extract stable reference to avoid dependency issues
  const setCurrentRoomRef = useCallback((room: any) => {
    // Use the current value at call time to avoid dependency issues
    if (roomContext?.setCurrentRoom) {
      roomContext.setCurrentRoom(room);
    }
  }, []); // Empty deps - capture roomContext at call time instead of in closure

  /**
   * Drop out of the island and back to the island list, telling the user why.
   * Shared by the three involuntary exits: removed by an admin, the permission
   * revoked underneath us, and guest access running out.
   */
  const leaveIslandWithNotice = useCallback((message: string) => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    setGuestExpiresAt(null);
    setConnectionState(prev => ({
      ...prev,
      isConnected: false,
      currentRoom: null,
      playersData: [],
      isLoading: false,
      error: message,
      sessionReplaced: false
    }));

    // Clearing the room in context is what navigates back to the island list.
    setCurrentRoomRef(null);

    // The island list is where the user lands, and it is what renders this.
    noticeRef.current?.setNotice(message);
  }, [setCurrentRoomRef]);

  // =====================================================================================
  // WEBSOCKET EVENT HANDLERS
  // =====================================================================================
  // This section contains all WebSocket event handling logic (43% of the file)
  // Handles: room_state, player_joined/left, status updates, zones, private areas

  const setupEventHandlers = useCallback((socket: Socket) => {
    // The id this socket connected with, for the socket-session registry.
    let connectedSocketId: string | undefined;

    // Handle complete room state (initial or reconnect)
    function handleRoomState(data: {
      data: RoomState;
      userId?: string;
      /** The joining user's own standing here, straight from the server. */
      role?: 'admin' | 'player' | 'guest' | null;
      expiresAt?: string | null;
    }) {
      console.info('📥 WS RECV: room_state - %d players, %d zones, %d private areas',
        data.data.players.length,
        data.data.zones?.length || 0,
        data.data.privateAreas?.length || 0,
        data
      );
      // Private areas received

      // Mark this as a full update since we're getting complete room state
      lastUpdateTypeRef.current = 'full';


      // Enrich players with isLocal flag (client-specific logic)
      const enrichedPlayers = enrichPlayerData(data.data.players, auth.user?.id);

      // Map backend zones to frontend HomeZone format
      // Backend now sends all zones (claimed and unclaimed) with 'id' field
      const zones: HomeZoneClaimState[] = (data.data.zones || []).map(zone => ({
        id: zone.id || zone.zoneId || '',  // Backend now sends 'id' directly
        // Backend doesn't send x,y,width,height - those come from tilemap
        claimedBy: zone.claimedBy || zone.userId,
        claimedByUsername: zone.claimedByUsername || zone.username,
        claimedByAvatar: zone.claimedByAvatar || zone.avatar,
        claimedByStatus: zone.claimedByStatus || zone.status,
        claimedByIsConnected: zone.claimedByIsConnected ?? zone.isConnected,
        claimedAt: zone.claimedAt
      }));

      const privateAreas = data.data.privateAreas || [];

      // The server answers every join — first one, room switch, reconnect —
      // with the user's own role and expiry, so the guest timer is armed from
      // the authority rather than from whatever the island list last said.
      setGuestExpiresAt(data.role === 'guest' ? data.expiresAt ?? null : null);

      setConnectionState(prev => ({
        ...prev,
        // Connected means joined: a socket that is back but not yet on the
        // island again would drop anything sent on it.
        isConnected: true,
        currentRoom: {
          ...data.data.room,
          ...(data.role !== undefined
            ? { currentUserRole: data.role ?? undefined, expiresAt: data.expiresAt ?? null }
            : {})
        },
        playersData: enrichedPlayers,
        homeZones: zones,
        privateAreas: privateAreas,
        isLoading: false,
        // A join is what takes a replaced session back.
        sessionReplaced: false
      }));

      // Update Phaser game with zone and private area data
      if (zones.length > 0) {
        updatePhaserZones(zones);
      }
      if (data.data.privateAreas) {
        updatePhaserPrivateAreas(data.data.privateAreas);
      }

      // Send decoration data to Phaser if available
      if (data.data.room.decorationsLayer && gameInstance) {
        const gameScene = phaserRoomService.getReadyGameScene(gameInstance);
        if (gameScene) {
          // Store decoration data in registry for GameScene to access
          gameScene.registry?.set('currentRoomData', data.data.room);
          // Emit event for GameScene to handle decorations
          gameScene.events?.emit('decorations:load', data.data.room.decorationsLayer);
        }
      }

      // Also update the React context room state for consistent routing
      if (data.data.room) {
        setCurrentRoomRef(data.data.room);
      }
    }

    // Handle new player joining  
    function handlePlayerJoined(data: { data: PlayerData }) {
      console.info('📥 WS RECV: player_joined - %s', data.data.username);
      
      
      // Mark this as a full update since we're adding a new player
      lastUpdateTypeRef.current = 'full';
      
      const enrichedPlayer = {
        ...data.data,
        isLocal: data.data.userId === auth.user?.id
      };
      
      setConnectionState(prev => {
        // Prevent duplicates
        const exists = prev.playersData.some(p => p.userId === enrichedPlayer.userId);
        if (exists) {
          console.warn('⚠️ Duplicate player join prevented: %s', enrichedPlayer.username);
          return prev;
        }
        
        const updatedPlayers = [...prev.playersData, enrichedPlayer];
        
        // Update zones if the joining player owns any
        const updatedZones = prev.homeZones.map(zone => {
          if (zone.claimedBy === enrichedPlayer.userId) {
            return {
              ...zone,
              claimedByIsConnected: true,
              claimedByAvatar: enrichedPlayer.avatar,
              claimedByStatus: enrichedPlayer.status
            };
          }
          return zone;
        });
        
        // Update Phaser game zone data if zones were updated
        const zonesChanged = updatedZones.some((zone, i) => 
          zone.claimedByIsConnected !== prev.homeZones[i].claimedByIsConnected
        );
        
        if (zonesChanged) {
          updatePhaserZones(updatedZones);
        }
        
        return { 
          ...prev, 
          playersData: updatedPlayers,
          homeZones: updatedZones
        };
      });
    }

    // Handle player leaving
    function handlePlayerLeft(data: { data: { userId: string; username?: string; wasRemoved?: boolean; removedBy?: string } }) {
      console.info('📥 WS RECV: player_left - %s', data.data.userId);
      
      setConnectionState(prev => {
        const updatedPlayers = prev.playersData.filter(p => p.userId !== data.data.userId);
        
        // Update zones if the leaving player owns any
        const updatedZones = prev.homeZones.map(zone => {
          if (zone.claimedBy === data.data.userId) {
            return {
              ...zone,
              claimedByIsConnected: false
            };
          }
          return zone;
        });
        
        // Update Phaser game zone data if zones were updated
        const zonesChanged = updatedZones.some((zone, i) => 
          zone.claimedByIsConnected !== prev.homeZones[i].claimedByIsConnected
        );
        
        if (zonesChanged) {
          updatePhaserZones(updatedZones);
        }
        
        return {
          ...prev,
          playersData: updatedPlayers,
          homeZones: updatedZones
        };
      });
      
      // Somebody else being removed is news about the island, not about this
      // session: the user is still standing in it, and the notice channel is
      // for the screen you are sent to. The member list is where it shows.
      if (data.data.wasRemoved && data.data.removedBy) {
        console.info(
          '📥 WS RECV: player_left - %s was removed by %s',
          data.data.username || 'a player',
          data.data.removedBy
        );
      }
    }


    // Handle status updates
    function handleStatusUpdated(data: { data: { userId: string; username: string; status: string } }) {
      console.info('📥 WS RECV: status_updated - %s: %s', data.data.username, data.data.status);
      
      // Mark this as a status-only update to avoid position updates
      lastUpdateTypeRef.current = 'status-only';
      
      setConnectionState(prev => {
        const updatedPlayers = prev.playersData.map(p => 
          p.userId === data.data.userId 
            ? { ...p, status: data.data.status }
            : p
        );

        // Update zones if the player whose status changed owns any
        const updatedZones = prev.homeZones.map(zone => {
          if (zone.claimedBy === data.data.userId) {
            return {
              ...zone,
              claimedByStatus: data.data.status
            };
          }
          return zone;
        });

        // Update Phaser game zone data if zones were updated
        const zonesChanged = updatedZones.some((zone, i) => 
          zone.claimedByStatus !== prev.homeZones[i].claimedByStatus
        );

        if (zonesChanged && isGameReady && gameInstance) {
          const gameScene = phaserRoomService.getReadyGameScene(gameInstance);
          if (gameScene?.updateZoneData) {
            gameScene.updateZoneData(updatedZones);
          }
        }

        return {
          ...prev,
          playersData: updatedPlayers,
          homeZones: updatedZones
        };
      });

      // Update name tag indicator directly in GameScene
      refreshPlayerNameTag(data.data.userId, data.data.status);

      // Resolve pending status change promise if this is for the current user
      if (data.data.userId === auth.user?.id && pendingStatusChangeRef.current) {
        clearTimeout(pendingStatusChangeRef.current.timeoutId);
        pendingStatusChangeRef.current.resolve();
        pendingStatusChangeRef.current = null;
      }
    }

    // Handle errors
    function handleError(data: {
      code?: string;
      message?: string;
      requestId?: string;
      scope?: string;
      data?: { message?: string };
    }) {
      // Every websocket handler emits a *flat* `message`; the nested
      // `data.message` shape is the older one a few payloads still use. Read
      // both, or the reason is dropped and the user gets a bare code (#77).
      const message = data.message ?? data.data?.message;

      console.error('❌ WS ERROR: %s', message || data.code || 'Unknown error');

      // Errors belonging to another domain are handled there (VideoContext), so
      // don't also surface them as a persistent room-connection banner.
      // `scope` carries the domain and `requestId` the request being answered;
      // a video error rejected before validation has the former but not the
      // latter (#84), so keying on `requestId` alone used to leak it here.
      if (data.requestId || data.scope === 'video') {
        return;
      }

      const errorMessage = message || `Connection error: ${data.code || 'Unknown error'}`;
      setConnectionState(prev => ({ ...prev, error: errorMessage, isLoading: false }));
      
      // Reject pending status change promise if there is one
      if (pendingStatusChangeRef.current) {
        clearTimeout(pendingStatusChangeRef.current.timeoutId);
        pendingStatusChangeRef.current.reject(new Error(errorMessage));
        pendingStatusChangeRef.current = null;
      }
    }

    // Handle connection events. `isConnected` turns true on the join's
    // `room_state`, not here: see handleRoomState.
    function handleConnect() {
      console.info('🔗 WS: Connected to server');
      // Captured here: socket.io drops `socket.id` before 'disconnect' fires.
      connectedSocketId = socket.id;
      setActiveSocketId(socket.id ?? null);
      setConnectionState(prev => ({ ...prev, error: null }));
    }

    function handleDisconnect() {
      console.info('💔 WS: Disconnected from server');
      clearActiveSocketId(connectedSocketId);
      connectedSocketId = undefined;
      setConnectionState(prev => ({ ...prev, isConnected: false }));
      
      // Reject any pending status change
      if (pendingStatusChangeRef.current) {
        clearTimeout(pendingStatusChangeRef.current.timeoutId);
        pendingStatusChangeRef.current.reject(new Error('Connection lost'));
        pendingStatusChangeRef.current = null;
      }
    }

    // Handle ping/pong for connection health
    function handlePing() {
      console.info('📥 WS RECV: ping');
      console.info('🚀 WS EMIT: pong');
      socket.emit('pong');
    }

    // Handle player being removed from room
    function handlePlayerRemoved(data: { roomId: string; removedBy: string; message: string }) {
      console.info('📥 WS RECV: player_removed - removed by %s', data.removedBy);

      // The server takes the socket off the island but leaves it connected, so
      // close it like every other exit: an open socket would join the island
      // again on its next automatic reconnect.
      leaveIslandWithNotice(data.message || 'You have been removed from the room');
    }

    /**
     * An admin changed somebody's island role (§6.4).
     *
     * Two things happen here: every open member list is told to refresh, and —
     * when the change is about the current user — the client updates its own
     * guest timer, or walks out of an island it no longer has a permission on.
     */
    function handleRoomRoleChanged(data: RoomRoleChangedEvent) {
      console.info('📥 WS RECV: room_role_changed - %s -> %s', data.userId, data.role ?? 'removed');

      publishRoomRoleChange(data);

      // The in-game chrome reads roles off `playersData` (the Admin button is
      // "am I an admin in this list?"), so a promotion has to land there too,
      // not only in whichever member list is open.
      setConnectionState(prev => {
        const next = applyRoomRoleChange(prev, data, auth.user?.id);
        return next === prev ? prev : { ...prev, ...next };
      });

      if (data.userId !== auth.user?.id) {
        return;
      }

      if (data.role === null) {
        leaveIslandWithNotice('You were removed from this island');
        return;
      }

      // Promotion out of the guest role clears the expiry, which stops the
      // timer; an extension pushes it out and reschedules it.
      setGuestExpiresAt(data.role === 'guest' ? data.expiresAt : null);
    }

    /**
     * The island is gone (§6.6). The same involuntary exit as being removed
     * from it: back to the island list with a reason, session untouched.
     */
    function handleRoomDeleted() {
      console.info('📥 WS RECV: room_deleted');
      leaveIslandWithNotice('This island was deleted');
    }

    /**
     * The account itself was deactivated (§6.5). The server has already revoked
     * every session and is about to drop every socket, so there is nothing left
     * to stay signed in for: leave the island for the notice, then log out, or
     * the client would sit on a dead token until its next rejected request.
     */
    function handleAccountDeactivated() {
      console.info('📥 WS RECV: account_deactivated');
      // The notice is set before the logout that wipes everything else, and
      // survives it: it lives above auth, so the sign-in modal can explain
      // why the user is looking at it.
      leaveIslandWithNotice('Your account has been deactivated');
      auth.logout?.();
    }

    /**
     * The account's password was reset or changed somewhere else, which
     * revoked every token issued before it — this one included. Like a
     * deactivation, there is nothing left to stay signed in for.
     */
    function handleSessionRevoked(data?: { tokenVersion?: number }) {
      console.info('📥 WS RECV: session_revoked');

      // This browser already stores a token issued under the new version (the
      // reset or change was made in another tab, or in this one). That session
      // is good: leave the island, but logging out would delete its token.
      const stored = localStorage.getItem('jwt_token');
      const storedVersion = stored ? readTokenVersion(stored) : null;
      if (
        typeof data?.tokenVersion === 'number' &&
        storedVersion !== null &&
        storedVersion >= data.tokenVersion
      ) {
        leaveIslandWithNotice('Your password was changed in another tab. Join the island again to continue.');
        return;
      }

      leaveIslandWithNotice('Your password was changed. Sign in with the new password.');
      auth.logout?.();
    }

    /**
     * The same account joined an island from another device or tab, and the
     * server has ended this session to make way for it (#132).
     *
     * Unlike the other involuntary exits this one stays on the island: the
     * user may want it back, and taking it back is a join of the island on
     * screen. The socket is let go of so that nothing re-joins by itself — two
     * devices doing that would take the session from each other forever.
     */
    function handleConnectionReplaced() {
      console.info('📥 WS RECV: connection_replaced');
      if (socketRef.current !== socket) {
        return;
      }

      socketRef.current.disconnect();
      socketRef.current = null;

      if (pendingStatusChangeRef.current) {
        clearTimeout(pendingStatusChangeRef.current.timeoutId);
        pendingStatusChangeRef.current.reject(new Error('Session opened elsewhere'));
        pendingStatusChangeRef.current = null;
      }

      setConnectionState(prev => ({ ...prev, isConnected: false, isLoading: false, sessionReplaced: true }));
    }

    /**
     * Somebody (possibly this user) picked a new character. RoomManager re-textures
     * the sprite off its own listener; React owns everything else that reads an
     * avatar — the member list, the player details modal and the owner bubble on a
     * claimed zone all take it from `playersData` / `homeZones`.
     */
    function handlePlayerAvatarUpdated(data: { data: { userId: string; username: string; avatar: string } }) {
      console.info('📥 WS RECV: player_avatar_updated - %s -> %s', data.data.username, data.data.avatar);

      // Nobody moved: keep this off the position path, as a status change does.
      lastUpdateTypeRef.current = 'status-only';

      setConnectionState(prev => ({
        ...prev,
        playersData: prev.playersData.map(p =>
          p.userId === data.data.userId ? { ...p, avatar: data.data.avatar } : p
        ),
        homeZones: prev.homeZones.map(zone =>
          zone.claimedBy === data.data.userId
            ? { ...zone, claimedByAvatar: data.data.avatar }
            : zone
        )
      }));
    }

    // Handle player teleported events - force position update for all players including local
    function handlePlayerTeleported(data: { data: { userId: string; position: { x: number; y: number }; animation: string; direction: string } }) {
      console.info('📥 WS RECV: player_teleported - %s to position %o', data.data.userId, data.data.position);
      
      // Force update player position in Phaser game via service
      if (isGameReady && gameInstance) {
        const gameScene = phaserRoomService.getReadyGameScene(gameInstance);
        if (gameScene?.roomManager) {
          // Use special method that forces local player updates
          gameScene.roomManager.handleTeleportUpdate(data.data.userId, data.data.position);
        }
      }
    }

    // Handle zone claimed events
    function handleZoneClaimed(data: { 
      data: { 
        zoneId: string; 
        claimedBy: string; 
        username: string; 
        claimedAt: string;
        claimedByAvatar?: string;
        claimedByStatus?: string;
        claimedByIsConnected?: boolean;
      } 
    }) {
      console.info('📥 WS RECV: zone_claimed - %s claimed by %s', data.data.zoneId, data.data.username);
      
      setConnectionState(prev => {
        const isLocalPlayerClaim = data.data.claimedBy === auth.user?.id;
        
        // Update zones - HomeZone uses 'id' field, not 'zoneId'
        const updatedZones = prev.homeZones.map(zone =>
          zone.id === data.data.zoneId
            ? {
                ...zone,
                claimedBy: data.data.claimedBy,
                claimedByUsername: data.data.username,
                claimedByAvatar: data.data.claimedByAvatar,
                claimedByStatus: data.data.claimedByStatus,
                claimedByIsConnected: data.data.claimedByIsConnected ?? (isLocalPlayerClaim ? true : undefined),
                claimedAt: data.data.claimedAt
              }
            : zone
        );

        // Update Phaser game zone data with updated zones (enriched with owner info)
        updatePhaserZones(updatedZones);

        return {
          ...prev,
          homeZones: updatedZones
        };
      });
    }

    // Handle zone unclaimed events
    function handleZoneUnclaimed(data: { data: { zoneId: string; previousOwner: string } }) {
      console.info('📥 WS RECV: zone_unclaimed - %s unclaimed', data.data.zoneId);
      
      setConnectionState(prev => {
        // Update zones - HomeZone uses 'id' field, not 'zoneId'
        const updatedZones = prev.homeZones.map(zone =>
          zone.id === data.data.zoneId
            ? {
                ...zone,
                claimedBy: undefined,
                claimedByUsername: undefined,
                claimedByAvatar: undefined,
                claimedByStatus: undefined,
                claimedByIsConnected: undefined,
                claimedAt: undefined
              }
            : zone
        );

        // Update Phaser game zone data with updated zones
        updatePhaserZones(updatedZones);

        return {
          ...prev,
          homeZones: updatedZones
        };
      });
    }

    // Handle zones initialized (when tilemap zones are sent to backend)
    function handleZonesInitialized(data: { data: { zones: any[] } }) {
      console.info('📥 WS RECV: zones_initialized - %d zones', data.data.zones.length);

      setConnectionState(prev => {
        // Map backend zones to frontend format
        const zones: HomeZoneClaimState[] = (data.data.zones || []).map(zone => ({
          id: zone.id || zone.zoneId || '',
          claimedBy: zone.claimedBy || zone.userId,
          claimedByUsername: zone.claimedByUsername || zone.username,
          claimedByAvatar: zone.claimedByAvatar || zone.avatar,
          claimedByStatus: zone.claimedByStatus || zone.status,
          claimedByIsConnected: zone.claimedByIsConnected ?? zone.isConnected,
          claimedAt: zone.claimedAt
        }));

        // Update Phaser game zone data
        updatePhaserZones(zones);

        return {
          ...prev,
          homeZones: zones
        };
      });
    }

    // Handle zones updated (broadcast from other clients)
    function handleZonesUpdated(data: { data: { zones: any[] } }) {
      console.info('📥 WS RECV: zones_updated - %d zones', data.data.zones.length);
      // Use same handler as zones_initialized
      handleZonesInitialized(data);
    }

    // Handle private area updates
    function handlePrivateAreaUpdate(message: { data: PrivateAreaUpdate }) {
      // Extract the nested data (consistent with other events like zone_claimed)
      const data = message.data;
      
      // Update local state
      setConnectionState(prev => {
        const updatedAreas = prev.privateAreas.map(area => 
          area.areaId === data.areaId ? data.area : area
        );
        
        // Add area if it doesn't exist
        if (!updatedAreas.find(a => a.areaId === data.areaId) && data.area) {
          updatedAreas.push(data.area);
        }
        
        return {
          ...prev,
          privateAreas: updatedAreas
        };
      });
      
      // Update Phaser game
      if (isGameReady && gameInstance) {
        const gameScene = phaserRoomService.getReadyGameScene(gameInstance);
        if (gameScene?.handlePrivateAreaUpdate) {
          gameScene.handlePrivateAreaUpdate(data);
        }
      }
    }

    // Handle private area door state updates
    function handlePrivateAreaDoorUpdate(message: { data: { areaId: string; isDoorOpen: boolean } }) {
      console.info('📥 WS RECV: private_area_door_update - area %s door is now %s', 
        message.data.areaId, 
        message.data.isDoorOpen ? 'open' : 'closed'
      );
      
      const { areaId, isDoorOpen } = message.data;
      
      // Update local state
      setConnectionState(prev => {
        const updatedAreas = prev.privateAreas.map(area => 
          area.areaId === areaId ? { ...area, isDoorOpen } : area
        );
        
        return {
          ...prev,
          privateAreas: updatedAreas
        };
      });
      
      // Update Phaser game scene
      handlePhaserDoorUpdate(areaId, isDoorOpen);
    }

    // Register all event listeners with standard Socket.IO pattern
    socket.on('room_state', handleRoomState);
    socket.on('player_joined', handlePlayerJoined);
    socket.on('player_left', handlePlayerLeft);
    socket.on('user_status_updated', handleStatusUpdated);
    socket.on('player_removed', handlePlayerRemoved);
    socket.on('room_role_changed', handleRoomRoleChanged);
    socket.on('room_deleted', handleRoomDeleted);
    socket.on('account_deactivated', handleAccountDeactivated);
    socket.on('session_revoked', handleSessionRevoked);
    socket.on('connection_replaced', handleConnectionReplaced);
    socket.on('player_avatar_updated', handlePlayerAvatarUpdated);
    socket.on('player_teleported', handlePlayerTeleported);
    socket.on('zone_claimed', handleZoneClaimed);
    socket.on('zone_unclaimed', handleZoneUnclaimed);
    socket.on('zones_initialized', handleZonesInitialized);
    socket.on('zones_updated', handleZonesUpdated);
    socket.on('private_area_update', handlePrivateAreaUpdate);
    socket.on('private_area_door_update', handlePrivateAreaDoorUpdate);
    socket.on('ping', handlePing);
    socket.on('error', handleError);
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);

    // Return cleanup function
    return () => {
      socket.off('room_state', handleRoomState);
      socket.off('player_joined', handlePlayerJoined);
      socket.off('player_left', handlePlayerLeft);
      socket.off('user_status_updated', handleStatusUpdated);
      socket.off('player_removed', handlePlayerRemoved);
      socket.off('room_role_changed', handleRoomRoleChanged);
      socket.off('room_deleted', handleRoomDeleted);
      socket.off('account_deactivated', handleAccountDeactivated);
      socket.off('session_revoked', handleSessionRevoked);
      socket.off('connection_replaced', handleConnectionReplaced);
      socket.off('player_avatar_updated', handlePlayerAvatarUpdated);
      socket.off('player_teleported', handlePlayerTeleported);
      socket.off('zone_claimed', handleZoneClaimed);
      socket.off('zone_unclaimed', handleZoneUnclaimed);
      socket.off('zones_initialized', handleZonesInitialized);
      socket.off('zones_updated', handleZonesUpdated);
      socket.off('private_area_update', handlePrivateAreaUpdate);
      socket.off('private_area_door_update', handlePrivateAreaDoorUpdate);
      socket.off('ping', handlePing);
      socket.off('error', handleError);
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
    };
  }, [auth.user?.id, auth.logout, setCurrentRoomRef, leaveIslandWithNotice, isGameReady, gameInstance, phaserRoomService]);

  // =====================================================================================
  // CONNECTION MANAGEMENT
  // =====================================================================================
  // Handles joining/leaving rooms, WebSocket connection lifecycle, and cleanup

  const joinRoom = useCallback(async (roomId: string): Promise<void> => {
    try {
      const token = localStorage.getItem('jwt_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      console.info('🎮 WS: Joining room %s', roomId);
      
      // Clean up any existing connection first to prevent duplicates
      if (socketRef.current) {
        console.info('🧹 WS: Cleaning up existing connection');
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      
      // CRITICAL: Clean up Phaser scene content when switching rooms
      // This ensures the old player sprite is destroyed and position is reset
      if (gameInstance) {
        const gameScene = phaserRoomService.getReadyGameScene(gameInstance);
        if (gameScene) {
          console.info('🎮 WS: Cleaning up Phaser scene content');
          phaserRoomService.cleanupRoomContent(gameScene);
        }
      }
      
      // Clear room initialization tracking for new room
      roomInitializedRef.current = null;
      
      setConnectionState(prev => ({ ...prev, isLoading: true, error: null }));

      // Create WebSocket connection
      const socket = io(WEBSOCKET_CONFIG.SERVER_URL, {
        // Read on every (re)connect, not once: a password change hands this
        // tab a new token, and reconnecting with the revoked one would fail.
        auth: (cb: (data: { token: string | null }) => void) => {
          cb({ token: localStorage.getItem('jwt_token') });
        },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: WEBSOCKET_CONFIG.RECONNECTION_DELAY,
        reconnectionDelayMax: WEBSOCKET_CONFIG.RECONNECTION_DELAY_MAX,
        timeout: WEBSOCKET_CONFIG.TIMEOUT
      } as WebSocketConnectionConfig);
      
      socketRef.current = socket;

      // Make socket available globally for GameScene
      (window as any).socket = socket;

      // Wait for connection and room join
      return new Promise((resolve, reject) => {
        let cleanupHandlers: (() => void) | null = null;
        let hasResolved = false;
        
        const timeoutId = setTimeout(() => {
          if (!hasResolved) {
            hasResolved = true;
            if (cleanupHandlers) cleanupHandlers();
            socket.disconnect();
            reject(new Error('Room join timeout'));
          }
        }, WEBSOCKET_CONFIG.TIMEOUT * 2);

        // Create room state handler that will resolve the promise
        // This only needs to resolve/reject - data processing happens in setupEventHandlers
        function handleRoomStateForJoin() {
          if (!hasResolved) {
            hasResolved = true;
            clearTimeout(timeoutId);
            console.info('✅ WS: Room joined successfully');
            // On SUCCESS: only clean up temporary handlers, keep permanent ones
            cleanupTemporaryHandlers();
            resolve();
          }
        }

        /**
         * Join the island on every connection this socket makes: the first
         * one, and each automatic reconnect after it. The server keeps no
         * memory of a dropped socket's island, so the new one has to ask again.
         *
         * It waits for `authenticated`, not `connect`: the server listens for
         * `join_room` only once it has authenticated the new socket, which
         * takes a database read after `connect` has already fired, and a join
         * sent before that is silently dropped.
         *
         * Only the socket this hook still holds may join. One it has let go of
         * (island left, replaced by another island, logged out) never takes the
         * user back.
         */
        function sendJoinRoom() {
          if (socketRef.current !== socket) {
            return;
          }
          console.info('🚀 WS EMIT: join_room');
          socket.emit('join_room', { roomId });
        }

        function handleConnectError(error: Error) {
          if (!hasResolved) {
            hasResolved = true;
            clearTimeout(timeoutId);
            if (cleanupHandlers) cleanupHandlers();
            socket.disconnect();
            reject(error);
          }
        }

        function handleError(data: any) {
          if (JOIN_REFUSAL_CODES.has(data?.code)) {
            if (!hasResolved) {
              hasResolved = true;
              clearTimeout(timeoutId);
              if (cleanupHandlers) cleanupHandlers();
              socket.disconnect();
              // Flat `message` first — that is what the handlers emit (#77).
              reject(new Error(data.message ?? data.data?.message ?? 'Room access denied'));
            }
          }
        }

        // Set up ALL event handlers BEFORE connecting
        // This ensures we don't miss any events
        const permanentCleanup = setupEventHandlers(socket);
        
        // Also register the room join completion handler
        socket.once('room_state', handleRoomStateForJoin);
        
        // Register connection handlers. The join itself is permanent: it has
        // to outlive the first join to re-join after a reconnect.
        socket.on('authenticated', sendJoinRoom);
        socket.on('connect_error', handleConnectError);
        socket.on('error', handleError);

        // Store cleanup functions - separate temporary vs permanent
        function cleanupTemporaryHandlers() {
          socket.off('room_state', handleRoomStateForJoin);
          socket.off('connect_error', handleConnectError);
          socket.off('error', handleError);
        }
        
        function cleanupAllHandlers() {
          cleanupTemporaryHandlers();
          socket.off('authenticated', sendJoinRoom);
          permanentCleanup();
        }
        
        // Store cleanup function for error/timeout cases
        cleanupHandlers = cleanupAllHandlers;
      });

    } catch (error) {
      console.error('useRoomConnection: Error joining room:', error);
      setConnectionState(prev => ({ 
        ...prev, 
        isLoading: false, 
        error: error instanceof Error ? error.message : 'Failed to join room' 
      }));
      throw error;
    }
  }, [setupEventHandlers, auth.user?.id]);

  /**
   * Take the session back from the device or tab that replaced it (#132): the
   * same join as from the island list, of the island still on screen. The
   * server ends the other session first and spawns this one where it stood.
   */
  const reclaimSession = useCallback(async (): Promise<void> => {
    const roomId = connectionState.currentRoom?.id;
    if (!roomId) {
      throw new Error('No island to take back');
    }
    await joinRoom(roomId);
  }, [connectionState.currentRoom?.id, joinRoom]);

  // =====================================================================================
  // ROOM CLEANUP & DISCONNECTION
  // =====================================================================================
  // Handles leaving rooms and cleaning up Phaser scene content (not destroying the game)

  const leaveRoom = useCallback(async (): Promise<void> => {
    console.info('🎮 WS: Leaving room');

    setGuestExpiresAt(null);

    // Cancel any pending status change
    if (pendingStatusChangeRef.current) {
      clearTimeout(pendingStatusChangeRef.current.timeoutId);
      pendingStatusChangeRef.current.reject(new Error('Room left'));
      pendingStatusChangeRef.current = null;
    }

    // Resolves once this client's socket is closed, so a caller that has to know
    // the island was left — the console, whose first act is to ask the server
    // who is online — can wait for it instead of firing the moment the exit was
    // asked for.
    //
    // What it proves is exactly that: the *client* socket closed. The server
    // handles `leave_room` and the disconnect that follows it on the same
    // connection, in that order, but nothing here waits for it to have done so.
    // A socket that never reports its close must not hang the caller either, so
    // whichever of the two comes first settles the promise and cancels the
    // other.
    let closed: Promise<void> = Promise.resolve();

    if (socketRef.current) {
      const socket = socketRef.current;
      closed = new Promise<void>(resolve => {
        function settle() {
          clearTimeout(fallbackId);
          socket.off?.('disconnect', settle);
          resolve();
        }
        const fallbackId = setTimeout(settle, LEAVE_TIMEOUT_MS);
        socket.once?.('disconnect', settle);
      });

      console.info('🚀 WS EMIT: leave_room');
      socket.emit('leave_room');
      socket.disconnect();
      socketRef.current = null;
    }

    // Clear Phaser scene content via service
    if (gameInstance) {
      const gameScene = phaserRoomService.getReadyGameScene(gameInstance);
      if (gameScene) {
        phaserRoomService.cleanupRoomContent(gameScene);
      }
    }
    
    // Clear room initialization tracking
    roomInitializedRef.current = null;

    setConnectionState({
      isConnected: false,
      currentRoom: null,
      playersData: [],
      homeZones: [],
      privateAreas: [],
      isLoading: false,
      error: null,
      sessionReplaced: false
    });

    await closed;
  }, []);

  // =====================================================================================
  // MESSAGE SENDING
  // =====================================================================================  
  // Send message function (for Phaser to use)
  const sendMessage = useCallback((event: string, data: any) => {
    // Fix state inconsistency if detected
    if (!socketRef.current && connectionState.isConnected) {
      console.warn('🔧 WS: Fixing connection state inconsistency');
      setConnectionState(prev => ({ ...prev, isConnected: false }));
    }
    
    safeEmit(socketRef.current, event, data);
  }, [connectionState.isConnected]);

  // Cleanup function
  const cleanup = useCallback(() => {
    setGuestExpiresAt(null);

    if (reconnectionTimeoutRef.current) {
      clearTimeout(reconnectionTimeoutRef.current);
      reconnectionTimeoutRef.current = null;
    }

    // Cancel any pending status change
    if (pendingStatusChangeRef.current) {
      clearTimeout(pendingStatusChangeRef.current.timeoutId);
      pendingStatusChangeRef.current.reject(new Error('Connection cleanup'));
      pendingStatusChangeRef.current = null;
    }

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    setConnectionState({
      isConnected: false,
      currentRoom: null,
      playersData: [],
      homeZones: [],
      privateAreas: [],
      isLoading: false,
      error: null,
      sessionReplaced: false
    });
  }, []);



  // =====================================================================================
  // PHASER SYNCHRONIZATION
  // =====================================================================================

  // Create Phaser game when user is authenticated
  useEffect(() => {
    if (auth.user?.id) {
      createGame();
    }
  }, [auth.user?.id, createGame]);

  // Track room initialization to prevent redundant initialization calls
  const roomInitializedRef = useRef<string | null>(null);

  // Initialize Phaser systems once per room join
  useEffect(() => {
    if (!connectionState.currentRoom || !auth.user?.id) {
      return;
    }

    if (!isGameReady || !gameInstance) {
      return;
    }

    const gameScene = phaserRoomService.getReadyGameScene(gameInstance);
    if (!gameScene) {
      return;
    }

    // Only initialize once per room
    const roomId = connectionState.currentRoom.id;
    if (roomInitializedRef.current !== roomId) {
      console.debug('🎮 Initializing Phaser systems for room:', roomId);
      
      // Initialize room manager with socket
      phaserRoomService.initializeRoomManager(gameScene, socketRef.current);
      
      // Initialize private area system - only once per room
      phaserRoomService.initializePrivateAreas(gameScene, socketRef.current, auth.user.id);
      
      roomInitializedRef.current = roomId;
    }

    // Update room content (can happen multiple times as state changes)
    // Note: Private area updates are handled via WebSocket events (handlePrivateAreaUpdate)
    // so we don't need to re-run this effect when privateAreas change
    const updateType = lastUpdateTypeRef.current;
    phaserRoomService.updateRoomContent(
      gameScene,
      connectionState.currentRoom,
      connectionState.playersData,
      connectionState.homeZones,
      connectionState.privateAreas,
      auth.user.id,
      updateType
    ).then(() => {
      lastUpdateTypeRef.current = 'full';
    });
  }, [
    connectionState.currentRoom,
    connectionState.playersData,
    connectionState.homeZones,
    // Removed connectionState.privateAreas - updates handled by WebSocket events
    auth.user?.id,
    isGameReady,
    gameInstance
  ]);

  // Control canvas visibility based on room state
  useEffect(() => {
    setCanvasVisible(!!connectionState.currentRoom);
  }, [connectionState.currentRoom, setCanvasVisible]);

  /**
   * The guest expiry timer (§6.2). It is a courtesy, not a control: the server
   * refuses a lapsed guest at join and at every REST call regardless. Its job is
   * to end a session that has quietly stopped being allowed, instead of leaving
   * the guest walking around an island where nothing works any more.
   *
   * Browser timers overflow past ~24.8 days, so a long window is walked in
   * legs: `computeGuestTimerDelay` says how long to wait and whether that wait
   * is the last one.
   */
  useEffect(() => {
    if (!connectionState.currentRoom || !guestExpiresAt) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    function scheduleNextLeg() {
      const plan = computeGuestTimerDelay(guestExpiresAt);
      if (!plan) {
        return;
      }

      timeoutId = setTimeout(() => {
        if (plan.isFinal) {
          leaveIslandWithNotice('Your guest access has expired');
        } else {
          scheduleNextLeg();
        }
      }, plan.delayMs);
    }

    scheduleNextLeg();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [connectionState.currentRoom, guestExpiresAt, leaveIslandWithNotice]);

  // Cleanup on unmount only - no dependencies to prevent premature cleanup
  useEffect(() => {
    return () => {
      // Access current values via refs to avoid stale closure issues
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      
      // Phaser cleanup is handled by usePhaserGame hook
    };
  }, []); // No dependencies - cleanup only on actual unmount

  // =====================================================================================
  // PLAYER ACTIONS
  // =====================================================================================
  // Handles user-initiated actions: status changes, teleportation

  const changeUserStatus = useCallback((newStatus: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!connectionState.isConnected || !connectionState.currentRoom) {
        reject(new Error('Cannot change status: not connected to room'));
        return;
      }

      if (!auth.user?.id) {
        reject(new Error('Cannot change status: user not authenticated'));
        return;
      }

      // Reject if there's already a pending status change
      if (pendingStatusChangeRef.current) {
        reject(new Error('Status change already in progress'));
        return;
      }

      // Set up timeout for the promise
      const timeoutId = setTimeout(() => {
        if (pendingStatusChangeRef.current) {
          pendingStatusChangeRef.current = null;
          reject(new Error('Status change timeout - server did not respond'));
        }
      }, 5000);

      // Store promise handlers for resolution by event handlers
      pendingStatusChangeRef.current = {
        resolve,
        reject,
        timeoutId
      };

      // Send the status change request
      sendMessage('user_status_change', createStatusChangePayload(connectionState.currentRoom.id, newStatus));
    });
  }, [connectionState.isConnected, connectionState.currentRoom, sendMessage, auth.user?.id]);

  // Function to teleport player via WebSocket - returns Promise
  const teleportPlayer = useCallback((targetUserId: string, position: { x: number; y: number }): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!connectionState.isConnected || !connectionState.currentRoom) {
        reject(new Error('Cannot teleport: not connected to room'));
        return;
      }

      if (!auth.user?.id) {
        reject(new Error('Cannot teleport: user not authenticated'));
        return;
      }

      // Send the teleport request with coordinates
      sendMessage('player_teleport', createTeleportPayload(targetUserId, position, connectionState.currentRoom.id));

      // For now, resolve immediately since we don't have a specific response handler
      // In the future, we could add a response handler similar to status changes
      resolve();
    });
  }, [connectionState.isConnected, connectionState.currentRoom, sendMessage, auth.user?.id]);

  // =====================================================================================
  // ZONE MANAGEMENT
  // =====================================================================================
  // Handles zone claiming and unclaiming through REST API calls

  const claimZone = useCallback(async (zoneId: string): Promise<void> => {
    if (!connectionState.isConnected || !connectionState.currentRoom) {
      throw new Error('Cannot claim zone: not connected to room');
    }

    if (!auth.user?.id) {
      throw new Error('Cannot claim zone: user not authenticated');
    }

    try {
      // Import roomAPI at the top of the file
      const roomAPI = await import('../services/room-api').then(m => m.default);
      await roomAPI.claimZone(connectionState.currentRoom.id, zoneId);
      
      // The WebSocket broadcast from the backend will update the state
    } catch (error) {
      console.error('Failed to claim zone:', error);
      throw error;
    }
  }, [connectionState.isConnected, connectionState.currentRoom, auth.user?.id]);

  // Zone unclaiming method
  const unclaimZone = useCallback(async (zoneId: string): Promise<void> => {
    if (!connectionState.isConnected || !connectionState.currentRoom) {
      throw new Error('Cannot unclaim zone: not connected to room');
    }

    if (!auth.user?.id) {
      throw new Error('Cannot unclaim zone: user not authenticated');
    }

    try {
      // Import roomAPI at the top of the file
      const roomAPI = await import('../services/room-api').then(m => m.default);
      await roomAPI.unclaimZone(connectionState.currentRoom.id, zoneId);
      
      // The WebSocket broadcast from the backend will update the state
    } catch (error) {
      console.error('Failed to unclaim zone:', error);
      throw error;
    }
  }, [connectionState.isConnected, connectionState.currentRoom, auth.user?.id]);

  // =====================================================================================
  // PRIVATE AREA MANAGEMENT
  // =====================================================================================
  // Handles private area door toggling via WebSocket

  const togglePrivateAreaDoor = useCallback(async (areaId: string): Promise<void> => {
    if (!connectionState.isConnected || !socketRef.current) {
      throw new Error('Not connected to room');
    }

    if (!connectionState.currentRoom) {
      throw new Error('No current room');
    }

    console.info('🚪 WS EMIT: private_area_door_toggle - area %s', areaId);
    
    // Emit the toggle event
    socketRef.current.emit('private_area_door_toggle', createDoorTogglePayload(connectionState.currentRoom.id, areaId));
  }, [connectionState.isConnected, connectionState.currentRoom]);

  // =====================================================================================
  // HOOK RETURN VALUE
  // =====================================================================================
  // Memoized return object with all state and methods exposed to components

  return useMemo(() => ({
    isConnected: connectionState.isConnected,
    currentRoom: connectionState.currentRoom,
    playersData: connectionState.playersData,
    homeZones: connectionState.homeZones,
    privateAreas: connectionState.privateAreas,
    isLoading: connectionState.isLoading,
    error: connectionState.error,
    sessionReplaced: connectionState.sessionReplaced,
    joinRoom,
    reclaimSession,
    leaveRoom,
    sendMessage,
    changeUserStatus,
    teleportPlayer,
    cleanup,
    claimZone,
    unclaimZone,
    togglePrivateAreaDoor,
    socket: socketRef.current
  }), [
    // Only include state values - functions created with useCallback are stable
    connectionState,
    joinRoom,
    reclaimSession,
    leaveRoom,
    sendMessage,
    changeUserStatus,
    teleportPlayer,
    cleanup,
    claimZone,
    unclaimZone,
    togglePrivateAreaDoor
  ]);
};